import React from 'react';
import { connect } from 'react-redux';
import { LabelsSidebar } from '../labels_sidebar/LabelsSidebar';
import { WorkOrderLabel } from '../../data/workOrderStarLabels';
import { getRecordsForTargetCollection } from '../../reducers/standard_reducer_utils';
import {
	workOrderLabelsRestCrudThunks,
	syncWorkOrderLabels,
} from '../../thunks/work_order_labels_thunks';
import { pickRandomLabelColor } from '../../constants/labelConstants';
import { isUserAllowedToAccess, PERMISSION_NAMES } from '../../utils/AuthUtils';
import { canAddWOLabelsOrStarNotes, nullSafeGetOrElse } from '../../utils/DataAccessUtils';

const LABELS_TARGET_COLLECTION = 'workOrderLabelsIndex';

interface OwnProps {
	visible?: boolean;
	onClose?: () => void;
	// Called after the label catalogue is successfully created/updated/deleted — lets
	// the Work Orders list refetch so row chips reflect renamed/recolored/removed labels.
	onLabelsCatalogueSaved?: () => void | Promise<void>;
	// When provided, the per-label count becomes clickable — the parent closes the
	// sidebar and applies that label as the Work Orders index filter (per Figma).
	onLabelCountClick?: (label: WorkOrderLabel) => void;
}

// Work-order flavor of the shared LabelsSidebar: tag icons, WO-count accessor,
// pastel palette, and the work_order_labels slice/thunks.
const WorkOrderLabelsManagementPage: React.FC<any> = (props) => (
	<LabelsSidebar
		{...props}
		getCount={(label) => nullSafeGetOrElse('associatedWorkOrdersCount', label, 0)}
		pickNewLabelColor={pickRandomLabelColor}
		deleteWarningEntityNoun="work orders"
	/>
);

const mapStateToProps = (state) => ({
	userType: state.session.userType,
	// Modifying the label catalogue (create/rename/delete) requires BOTH the access
	// permission AND the Pro entitlement. Without the Pro check, an unpaid supplier on
	// the default permission could rename/delete labels here and indirectly strip them
	// from work orders — bypassing the upsell the rest of the Stars + Labels UI enforces.
	canModifyLabels:
		isUserAllowedToAccess(state.session.userType)(
			PERMISSION_NAMES.MODIFY_WORK_ORDER_LABELS,
			nullSafeGetOrElse('company_config.detail', state, {}),
			nullSafeGetOrElse('session.currentUser.roles', state, [])
		) && canAddWOLabelsOrStarNotes(state.session.currentUser),
	labels: getRecordsForTargetCollection(state.work_order_labels, LABELS_TARGET_COLLECTION),
	fetching: nullSafeGetOrElse(
		`${LABELS_TARGET_COLLECTION}.fetching`,
		state.work_order_labels,
		false
	),
});
const mapDispatchToProps = (dispatch) => ({ dispatch });
const mergeProps = (stateProps, dispatchProps, ownProps: OwnProps) => {
	const { userType, canModifyLabels, labels, fetching } = stateProps;
	const { dispatch } = dispatchProps;
	const thunks = workOrderLabelsRestCrudThunks(userType);
	return {
		...ownProps,
		canModifyLabels,
		labels,
		fetching,
		fetchAllLabels: () =>
			dispatch(
				thunks.read({ no_pagination: true }, LABELS_TARGET_COLLECTION, null, {
					sort_by: 'associatedWorkOrdersCount',
					order: 'desc',
				})
			),
		syncLabels: (entries) =>
			dispatch(
				syncWorkOrderLabels(userType)(entries, {
					history: (ownProps as any).history,
					targetCollectionName: (ownProps as any).targetCollectionName,
				})
			),
		onLabelsCatalogueSaved: () => {
			if (ownProps.onLabelsCatalogueSaved) {
				return ownProps.onLabelsCatalogueSaved();
			}
		},
	};
};

export default connect(
	mapStateToProps,
	mapDispatchToProps,
	mergeProps
)(WorkOrderLabelsManagementPage);
